// Presenza delle tab "main" dell'app rilevata via Web Locks invece che via timer.
// Un Web Lock TENUTO non è soggetto al throttling dei timer in background: una tab
// main viva (anche se throttlata/congelata) continua a tenere il lock. Così il popup
// SIP distingue "tutte le main chiuse" da "main solo in background" ed evita di
// auto-chiudersi per falso positivo.
// No-op / null dove l'API non è disponibile (→ il chiamante usa il fallback a timer).

const MAIN_PRESENCE_LOCK = "gaia-main-presence";

/**
 * Da chiamare in ogni tab MAIN (non nel popup). Acquisisce un lock condiviso e lo
 * tiene finché non si invoca la funzione di release ritornata — o finché il browser
 * lo rilascia da sé a chiusura/crash/navigazione della tab. Ritorna un no-op se
 * l'API Web Locks non è disponibile.
 */
export function holdMainPresence(): () => void {
  if (!("locks" in navigator)) return () => {};

  let release = (): void => {};
  // request() risolve subito per i lock 'shared'; la callback tiene il lock vivo
  // finché la sua promise non si risolve (= quando chiamiamo release()).
  navigator.locks
    .request(
      MAIN_PRESENCE_LOCK,
      { mode: "shared" },
      () =>
        new Promise<void>((resolve) => {
          release = resolve;
        })
    )
    .catch(() => {
      /* lock non acquisito: best-effort, nessuna azione */
    });

  return () => release();
}

/**
 * Da chiamare nel POPUP. Ritorna:
 * - true  → almeno una tab main tiene il lock di presenza (viva, magari throttlata)
 * - false → nessuna tab main presente
 * - null  → API Web Locks non disponibile (il chiamante deve usare il fallback a timer)
 */
export async function isAnyMainPresent(): Promise<boolean | null> {
  if (!("locks" in navigator)) return null;
  try {
    const state = await navigator.locks.query();
    return (state.held ?? []).some((lock) => lock.name === MAIN_PRESENCE_LOCK);
  } catch {
    return null;
  }
}
